<template>
  <div class="course-process-page">
    <div v-if="loading" class="status-card">正在加载教学流程...</div>
    <div v-else-if="errorMessage" class="status-card is-error">
      {{ errorMessage }}
    </div>
    <template v-else>
      <!-- 流程统计 -->
      <section class="process-stats panel">
        <div class="process-stats__heading">
          <h2 class="section-title">{{ processData.course_title }}</h2>
          <router-link
            class="btn btn--primary"
            :to="{ name: 'homework-list', params: { courseId } }"
          >
            查看课程作业
          </router-link>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <span class="stat-icon">📚</span>
            <div class="stat-content">
              <strong>{{ processData.chapter_count }}</strong>
              <span>章节</span>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-icon">📄</span>
            <div class="stat-content">
              <strong>{{ processData.section_count }}</strong>
              <span>小节</span>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-icon">🎬</span>
            <div class="stat-content">
              <strong>{{ processData.lesson_count }}</strong>
              <span>课时</span>
            </div>
          </div>
          <div class="stat-item">
            <span class="stat-icon">📝</span>
            <div class="stat-content">
              <strong>{{ processData.exam_count }}</strong>
              <span>考试</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 教学流程树 -->
      <section class="process-tree panel">
        <h2 class="section-title">教学流程</h2>
        <div class="chapter-list">
          <div
            v-for="(chapter, chapterIndex) in processData.chapter_list"
            :key="chapter.id"
            class="chapter-node"
          >
            <!-- 章节头部 -->
            <div class="node-header chapter-header" @click="toggleChapter(chapter.id)">
              <div class="node-info">
                <span class="node-number">{{ chapterIndex + 1 }}</span>
                <div class="node-content">
                  <h3 class="node-title">{{ chapter.title }}</h3>
                  <p v-if="chapter.intro" class="node-intro">{{ chapter.intro }}</p>
                  <div class="node-meta">
                    <span class="meta-tag">{{ chapter.lesson_count }} 课时</span>
                    <span class="meta-tag">{{ formatDuration(chapter.duration) }}</span>
                    <span v-if="chapter.status === 1" class="meta-tag meta-tag--success">已发布</span>
                  </div>
                </div>
              </div>
              <span
                class="toggle-icon"
                :class="{ 'is-open': expandedChapters.includes(chapter.id) }"
              >
                ▼
              </span>
            </div>

            <!-- 章节考试 -->
            <div v-if="chapter.has_exam && chapter.exam_plan" class="exam-card">
              <div class="exam-card__header">
                <span class="exam-icon">📝</span>
                <strong>章节考试</strong>
              </div>
              <div class="exam-card__body">
                <p class="exam-title">{{ chapter.exam_plan.paper_title }}</p>
                <div class="exam-info">
                  <span>题目数：{{ chapter.exam_plan.paper_question_count }}</span>
                  <span>总分：{{ chapter.exam_plan.paper_total_score }}</span>
                  <span>及格分：{{ chapter.exam_plan.pass_score }}</span>
                  <span v-if="chapter.exam_plan.exam_minutes > 0">
                    时长：{{ chapter.exam_plan.exam_minutes }}分钟
                  </span>
                  <span v-else>不限时</span>
                </div>
                <p v-if="chapter.exam_plan.description" class="exam-desc">
                  {{ chapter.exam_plan.description }}
                </p>
                <button class="btn btn--primary btn--sm" @click="startExam(chapter.exam_plan)">
                  开始考试
                </button>
              </div>
            </div>

            <!-- 小节列表 -->
            <div
              v-if="expandedChapters.includes(chapter.id) && chapter.children"
              class="section-list"
            >
              <div
                v-for="(section, sectionIndex) in chapter.children"
                :key="section.id"
                class="section-node"
              >
                <!-- 小节头部 -->
                <div class="node-header section-header" @click="toggleSection(section.id)">
                  <div class="node-info">
                    <span class="node-number">{{ chapterIndex + 1 }}.{{ sectionIndex + 1 }}</span>
                    <div class="node-content">
                      <h4 class="node-title">{{ section.title }}</h4>
                      <p v-if="section.intro" class="node-intro">{{ section.intro }}</p>
                      <div class="node-meta">
                        <span class="meta-tag">{{ section.lesson_count }} 课时</span>
                        <span class="meta-tag">{{ formatDuration(section.duration) }}</span>
                      </div>
                    </div>
                  </div>
                  <span
                    class="toggle-icon"
                    :class="{ 'is-open': expandedSections.includes(section.id) }"
                  >
                    ▼
                  </span>
                </div>

                <!-- 小节考试 -->
                <div v-if="section.has_exam && section.exam_plan" class="exam-card exam-card--small">
                  <div class="exam-card__header">
                    <span class="exam-icon">📝</span>
                    <strong>小节考试</strong>
                  </div>
                  <div class="exam-card__body">
                    <p class="exam-title">{{ section.exam_plan.paper_title }}</p>
                    <div class="exam-info">
                      <span>{{ section.exam_plan.paper_question_count }}题</span>
                      <span>{{ section.exam_plan.paper_total_score }}分</span>
                      <span>及格：{{ section.exam_plan.pass_score }}分</span>
                    </div>
                    <button class="btn btn--primary btn--sm" @click="startExam(section.exam_plan)">
                      开始考试
                    </button>
                  </div>
                </div>

                <!-- 课时列表 -->
                <div
                  v-if="expandedSections.includes(section.id) && section.lessons"
                  class="lesson-list"
                >
                  <div
                    v-for="(lesson, lessonIndex) in section.lessons"
                    :key="lesson.id"
                    class="lesson-item"
                    @click="handleLessonClick(lesson)"
                  >
                    <span class="lesson-number">{{ lessonIndex + 1 }}</span>
                    <div class="lesson-content">
                      <h5 class="lesson-title">{{ lesson.title }}</h5>
                      <div v-if="formatLessonTags(lesson).length" class="lesson-tags">
                        <span
                          v-for="tag in formatLessonTags(lesson)"
                          :key="tag"
                          class="lesson-tag"
                        >
                          {{ tag }}
                        </span>
                      </div>
                      <div class="lesson-meta">
                        <span class="lesson-type" :class="'lesson-type--' + lesson.type">
                          {{ lesson.type_word }}
                        </span>
                        <span v-if="lesson.status === 1" class="lesson-status lesson-status--published">
                          {{ lesson.status_word }}
                        </span>
                      </div>
                    </div>
                    <span class="lesson-arrow">→</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { fetchCourseProcessTree } from '@/api/courseProcess'

export default {
  name: 'CourseProcess',
  props: {
    courseId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      processData: {
        course_id: null,
        course_title: '',
        chapter_count: 0,
        section_count: 0,
        lesson_count: 0,
        exam_count: 0,
        chapter_list: []
      },
      expandedChapters: [],
      expandedSections: []
    }
  },
  mounted() {
    this.loadProcessData()
  },
  methods: {
    async loadProcessData() {
      this.loading = true
      this.errorMessage = ''

      try {
        const data = await fetchCourseProcessTree(this.courseId)
        this.processData = data

        // 默认展开第一个章节和第一个小节
        if (data.chapter_list && data.chapter_list.length > 0) {
          this.expandedChapters = [data.chapter_list[0].id]
          if (data.chapter_list[0].children && data.chapter_list[0].children.length > 0) {
            this.expandedSections = [data.chapter_list[0].children[0].id]
          }
        }
      } catch (error) {
        console.error('加载教学流程失败:', error)
        this.errorMessage = error.message || '加载失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    toggleChapter(chapterId) {
      const index = this.expandedChapters.indexOf(chapterId)
      if (index > -1) {
        this.expandedChapters.splice(index, 1)
      } else {
        this.expandedChapters.push(chapterId)
      }
    },
    toggleSection(sectionId) {
      const index = this.expandedSections.indexOf(sectionId)
      if (index > -1) {
        this.expandedSections.splice(index, 1)
      } else {
        this.expandedSections.push(sectionId)
      }
    },
    handleLessonClick(lesson) {
      // 跳转到课时学习页面
      this.$emit('lesson-click', lesson)
    },
    formatLessonTags(lesson) {
      const tags = lesson && Array.isArray(lesson.tags) ? lesson.tags : []
      return tags
        .filter(tag => typeof tag === 'string' && tag.trim())
        .map(tag => tag.trim())
    },
    startExam(examPlan) {
      // 跳转到考试页面
      if (examPlan.process_exam_id) {
        this.$router.push({
          name: 'exam',
          params: { processExamId: examPlan.process_exam_id }
        })
      } else {
        alert('考试配置ID不存在')
      }
    },
    formatDuration(seconds) {
      if (!seconds) return '0分钟'

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)

      if (hours > 0) {
        return `${hours}小时${minutes}分钟`
      }
      return `${minutes}分钟`
    }
  }
}
</script>

<style scoped>
.course-process-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.status-card {
  padding: 40px;
  text-align: center;
  background: #f5f7fa;
  border-radius: 8px;
  color: #606266;
}

.status-card.is-error {
  background: #fef0f0;
  color: #f56c6c;
}

.panel {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: #303133;
}

.process-stats__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.process-stats__heading .section-title {
  margin-bottom: 0;
}

@media (max-width: 640px) {
  .process-stats__heading {
    align-items: flex-start;
    flex-direction: column;
  }
}

/* 统计卡片 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.stat-icon {
  font-size: 32px;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-content strong {
  font-size: 24px;
  color: #303133;
}

.stat-content span {
  font-size: 14px;
  color: #909399;
}

/* 节点样式 */
.chapter-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chapter-node {
  border: 2px solid #e4e7ed;
  border-radius: 12px;
  overflow: hidden;
}

.node-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  cursor: pointer;
  transition: background 0.2s;
}

.node-header:hover {
  background: #f5f7fa;
}

.chapter-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.chapter-header:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a4291 100%);
}

.section-header {
  background: #f5f7fa;
}

.node-info {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
}

.node-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-size: 18px;
  font-weight: 600;
  flex-shrink: 0;
}

.section-header .node-number {
  background: #409eff;
  color: #fff;
}

.node-content {
  flex: 1;
}

.node-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.section-header .node-title {
  font-size: 16px;
  color: #303133;
}

.node-intro {
  font-size: 14px;
  margin: 0 0 8px 0;
  opacity: 0.9;
}

.section-header .node-intro {
  color: #606266;
}

.node-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-tag {
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  font-size: 12px;
}

.section-header .meta-tag {
  background: #e4e7ed;
  color: #606266;
}

.meta-tag--success {
  background: rgba(103, 194, 58, 0.2);
}

.toggle-icon {
  font-size: 12px;
  transition: transform 0.3s;
}

.toggle-icon.is-open {
  transform: rotate(180deg);
}

/* 考试卡片 */
.exam-card {
  margin: 16px;
  padding: 16px;
  background: #fff3e0;
  border: 2px solid #ffb74d;
  border-radius: 8px;
}

.exam-card--small {
  background: #e3f2fd;
  border-color: #64b5f6;
}

.exam-card__header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.exam-icon {
  font-size: 20px;
}

.exam-card__header strong {
  font-size: 14px;
  color: #303133;
}

.exam-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #303133;
}

.exam-info {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 14px;
  color: #606266;
  margin-bottom: 8px;
}

.exam-desc {
  font-size: 14px;
  color: #909399;
  margin: 0 0 12px 0;
}

.btn--sm {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn--primary {
  background: #409eff;
  color: #fff;

  &:hover {
    background: #66b1ff;
  }
}

/* 小节和课时列表 */
.section-list {
  background: #fafafa;
  padding: 16px;
}

.section-node {
  background: #fff;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.section-node:last-child {
  margin-bottom: 0;
}

.lesson-list {
  padding: 8px 16px 16px;
  background: #fff;
}

.lesson-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.lesson-item:hover {
  background: #e4e7ed;
  transform: translateX(4px);
}

.lesson-item:last-child {
  margin-bottom: 0;
}

.lesson-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #409eff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.lesson-content {
  flex: 1;
}

.lesson-title {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px 0;
  color: #303133;
}

.lesson-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.lesson-tag {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ecf5ff;
  color: #409eff;
  font-size: 12px;
  line-height: 1.35;
}

.lesson-meta {
  display: flex;
  gap: 8px;
}

.lesson-type {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.lesson-type--1 {
  background: #e3f2fd;
  color: #1976d2;
}

.lesson-type--2 {
  background: #f3e5f5;
  color: #7b1fa2;
}

.lesson-status--published {
  color: #67c23a;
  font-size: 12px;
}

.lesson-arrow {
  color: #909399;
  font-size: 16px;
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .node-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .node-info {
    width: 100%;
  }
}
</style>
